import { useNavigate } from "react-router-dom";
import { DollarSign, ExternalLink, ArrowRight } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

// SA-relevant funding data sourced from AEC Transparency Register
const SA_FUNDING = [
  {
    party: "SA Labor (ALP)",
    color: "#CC2529",
    total: "$4.2M",
    topSources: [
      { name: "SDA Union (SA Branch)", type: "Union", amount: "$820K" },
      { name: "Australian Workers Union", type: "Union", amount: "$540K" },
      { name: "CFMMEU", type: "Union", amount: "$480K" },
    ],
    dominantType: "Union affiliation fees",
    transparency: "78%",
  },
  {
    party: "SA Liberal Party",
    color: "#1B4F9E",
    total: "$2.9M",
    topSources: [
      { name: "Cormack Foundation (SA)", type: "Trust/Corporate", amount: "$920K" },
      { name: "Property Council SA", type: "Industry Body", amount: "$320K" },
      { name: "Various Business Donors", type: "Corporate", amount: "$580K" },
    ],
    dominantType: "Business & trust donations",
    transparency: "64%",
  },
];

export function SAFundingWidget() {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const textColor = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";
  const borderC = isDark ? "rgba(51,65,85,0.55)" : "rgba(226,232,240,0.85)";
  const shadowMd = isDark ? "0 4px 20px -2px rgba(0,0,0,0.35)" : "0 4px 16px -2px rgba(15,23,42,0.07)";
  const subBg = isDark ? "#0F172A" : "#F8FAFC";

  return (
    <section data-testid="sa-funding-section">
      <div className="flex items-center gap-2.5 mb-6">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(234,179,8,0.12)", border: "1px solid rgba(234,179,8,0.22)" }}
        >
          <DollarSign style={{ width: 18, height: 18, color: "#EAB308" }} />
        </div>
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: textColor, fontFamily: "Outfit, sans-serif" }}>
            Who Funds This Election?
          </h2>
          <p className="text-sm" style={{ color: textSec }}>
            SA party funding sources — AEC Transparency Register data
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {SA_FUNDING.map((party) => (
          <div
            key={party.party}
            style={{
              background: surface,
              border: `1px solid ${borderC}`,
              borderRadius: 16,
              boxShadow: shadowMd,
              overflow: "hidden",
            }}
            data-testid={`sa-funding-${party.party.toLowerCase().includes("labor") ? "alp" : "liberal"}`}
          >
            {/* Color bar */}
            <div style={{ height: 3, background: `linear-gradient(90deg, ${party.color}, ${party.color}88)` }} />

            <div style={{ padding: "16px 18px" }}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-base font-bold" style={{ color: party.color, fontFamily: "Outfit, sans-serif" }}>
                    {party.party}
                  </h3>
                  <p className="text-xs" style={{ color: textSec }}>
                    Dominant source: {party.dominantType}
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span className="text-xl font-black" style={{ color: textColor, fontFamily: "Outfit, sans-serif" }}>
                    {party.total}
                  </span>
                  <p className="text-xs" style={{ color: textSec }}>declared (FY23–24)</p>
                </div>
              </div>

              {/* Top donors */}
              <div
                className="rounded-xl p-3 space-y-2"
                style={{ background: isDark ? "rgba(15,23,42,0.6)" : "#F8FAFC", border: `1px solid ${borderC}` }}
              >
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: textSec }}>
                  Top declared donors
                </p>
                {party.topSources.map((src) => (
                  <div key={src.name} className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium" style={{ color: textColor }}>{src.name}</span>
                      <span
                        className="text-xs ml-2"
                        style={{
                          padding: "1px 7px",
                          borderRadius: 99,
                          background: `${party.color}14`,
                          color: party.color,
                        }}
                      >
                        {src.type}
                      </span>
                    </div>
                    <span className="text-xs font-bold" style={{ color: textColor }}>{src.amount}</span>
                  </div>
                ))}
              </div>

              {/* Transparency score */}
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs" style={{ color: textSec }}>AEC transparency score</span>
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{
                    background: "rgba(16,185,129,0.10)",
                    color: "#10B981",
                    border: "1px solid rgba(16,185,129,0.20)",
                  }}
                >
                  {party.transparency}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Disclaimer + CTA */}
      <div
        className="rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        style={{ background: subBg, border: `1px solid ${borderC}` }}
      >
        <div>
          <p className="text-xs font-medium mb-1" style={{ color: textColor }}>
            Sourced from AEC Transparency Register 2022–24
          </p>
          <p className="text-xs leading-relaxed" style={{ color: textSec }}>
            Figures represent declared receipts above the AEC disclosure threshold (~$16,900/year).
            Actual party income is higher. Data does not include undeclared small donors.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <a
            href="https://transparency.aec.gov.au"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-all"
            style={{
              background: isDark ? "rgba(51,65,85,0.4)" : "#F1F5F9",
              color: textSec,
              border: `1px solid ${borderC}`,
              textDecoration: "none",
            }}
          >
            AEC Source <ExternalLink style={{ width: 11, height: 11 }} />
          </a>
          <button
            onClick={() => navigate("/funding")}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-all"
            style={{
              background: "linear-gradient(135deg, #0D5C3A, #10B981)",
              color: "#fff",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(13,92,58,0.22)",
            }}
            data-testid="explore-funding-btn"
          >
            Explore Full Funding <ArrowRight style={{ width: 11, height: 11 }} />
          </button>
        </div>
      </div>
    </section>
  );
}
